//new code --notification-modal, --------------------issue solved


"use client"

import { useState, useEffect, useRef, useCallback } from "react"

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"

import { Badge } from "../ui/badge"

import { Bell, CheckCircle, XCircle, Clock, UserCheck, Calendar, X } from "lucide-react"

import { Button } from "../ui/button"

interface NotificationModalProps {

  isOpen: boolean

  onClose: () => void

  onNotificationCountChange?: (count: number) => void

}

// Backend notification structure (matches actual API response)
interface BackendNotification {
  recordId: number
  userId: number
  notificationId: number
  title: string
  description: string
  icon: string
  isRead: boolean
  isActive: boolean
  createdOn: string
}

// Frontend notification structure
interface Notification {
  id: number
  title: string
  message: string
  time: string
  icon: typeof CheckCircle
  color: string
  border: string
  reason?: string
}

// Icon mapping from backend string to lucide-react icons
const iconMap: Record<string, typeof CheckCircle> = {
  "CheckCircle": CheckCircle,
  "checkcircle": CheckCircle,
  "XCircle": XCircle,
  "xcircle": XCircle,
  "exclamation circle red": XCircle,
  "exclamation circle": XCircle,
  "Clock": Clock,
  "clock": Clock,
  "UserCheck": UserCheck,
  "usercheck": UserCheck,
  "Calendar": Calendar,
  "calendar": Calendar,
  "Bell": Bell,
  "bell": Bell,
}

// Color mapping based on icon type
const getColorAndBorder = (iconName: string): { color: string; border: string } => {
  const lowerIcon = iconName.toLowerCase()
  if (lowerIcon.includes("check") || lowerIcon.includes("approved")) {
    return { color: "text-green-600", border: "border-green-300" }
  }
  if (lowerIcon.includes("x") || lowerIcon.includes("reject") || lowerIcon.includes("error")) {
    return { color: "text-red-600", border: "border-red-200" }
  }
  if (lowerIcon.includes("clock") || lowerIcon.includes("pending") || lowerIcon.includes("reminder")) {
    return { color: "text-orange-600", border: "border-orange-200" }
  }
  if (lowerIcon.includes("calendar") || lowerIcon.includes("leave")) {
    return { color: "text-blue-600", border: "border-blue-300" }
  }
  if (lowerIcon.includes("user") || lowerIcon.includes("approver")) {
    return { color: "text-purple-600", border: "border-purple-200" }
  }
  return { color: "text-gray-600", border: "border-gray-300" }
}

// Format time relative to now
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "Just now"
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`
  return date.toLocaleDateString()
}

// Convert backend notification to frontend format
const mapNotification = (backendNotif: BackendNotification): Notification => {
  const IconComponent = iconMap[backendNotif.icon] || Bell
  const { color, border } = getColorAndBorder(backendNotif.icon)

  return {
    id: backendNotif.recordId,
    title: backendNotif.title,
    message: backendNotif.description,
    time: formatTimeAgo(backendNotif.createdOn),
    icon: IconComponent,
    color,
    border,
  }
}

export function NotificationModal({ isOpen, onClose, onNotificationCountChange }: NotificationModalProps) {

  const [notifList, setNotifList] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Smart polling state
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden)
  const [isUserActive, setIsUserActive] = useState(true)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const countFetchInProgressRef = useRef<boolean>(false)
  const countDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastCountFetchRef = useRef<number>(0)
  const dismissAllCalledRef = useRef<boolean>(false)

  // Fetch notification count from API (with deduplication and debouncing)
  const fetchNotificationCount = useCallback(async (immediate = false) => {
    // Clear existing debounce timeout if not immediate
    if (!immediate && countDebounceTimeoutRef.current) {
      clearTimeout(countDebounceTimeoutRef.current)
    }

    const executeFetch = async () => {
      // Prevent duplicate simultaneous requests
      if (countFetchInProgressRef.current) {
        return
      }

      // Throttle: don't fetch if we fetched in the last 500ms
      const now = Date.now()
      if (!immediate && now - lastCountFetchRef.current < 500) {
        return
      }

      countFetchInProgressRef.current = true
      lastCountFetchRef.current = now

      try {
        const response = await fetch(process.env.REACT_APP_API_URL + "/api/notification/count", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch notification count: ${response.status}`)
        }
        
        const data: { NotificationCount?: number; notificationCount?: number } = await response.json()
        // Notify parent of count change (handle both PascalCase and camelCase)
        if (onNotificationCountChange) {
          const count = data.NotificationCount ?? data.notificationCount ?? 0
          onNotificationCountChange(count)
        }
      } catch (err: any) {
        console.error("Error fetching notification count:", err)
      } finally {
        countFetchInProgressRef.current = false
      }
    }

    if (immediate) {
      await executeFetch()
    } else {
      // Debounce: wait 300ms before fetching
      countDebounceTimeoutRef.current = setTimeout(executeFetch, 300)
    }
  }, [onNotificationCountChange])

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (shouldFetchCount = true) => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(process.env.REACT_APP_API_URL + "/api/notification", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`)
      }
      
      const data: BackendNotification[] = await response.json()
      console.log("Fetched notifications:", data) // Debug log
      const notifications = data
        .filter((n: BackendNotification) => n.isActive) // Only show active notifications
        .map(mapNotification)
      console.log("Mapped notifications:", notifications) // Debug log
      setNotifList(notifications)
      // Fetch count separately from count API (only if requested)
      if (shouldFetchCount) {
        fetchNotificationCount()
      }
    } catch (err: any) {
      console.error("Error fetching notifications:", err)
      setError(err?.message || "Failed to fetch notifications")
    } finally {
      setLoading(false)
    }
  }, [fetchNotificationCount])

  // Dismiss (mark as inactive) a single notification
  const dismissNotification = async (id: number) => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + `/api/notification/inactive/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      
      if (!response.ok) {
        throw new Error(`Failed to dismiss notification: ${response.status}`)
      }
      
      setNotifList((prev) => prev.filter((n) => n.id !== id))
      // Fetch updated count from API
      fetchNotificationCount()
    } catch (err: any) {
      console.error("Error dismissing notification:", err)
      // Still remove from UI on error for better UX
      setNotifList((prev) => prev.filter((n) => n.id !== id))
      fetchNotificationCount()
    }
  }

  // Mark all notifications as read (called when modal closes)
  const markAllAsRead = useCallback(async () => {
    // Skip if dismissAll was just called (it already handles count update)
    if (dismissAllCalledRef.current) {
      dismissAllCalledRef.current = false
      return
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/api/notification/read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      
      if (!response.ok) {
        throw new Error(`Failed to mark notifications as read: ${response.status}`)
      }
      
      // Fetch updated count from API
      fetchNotificationCount()
    } catch (err: any) {
      console.error("Error marking notifications as read:", err)
    }
  }, [fetchNotificationCount])

  // Dismiss all notifications (make all inactive)
  const dismissAll = async () => {
    // Set flag to prevent markAllAsRead from running
    dismissAllCalledRef.current = true
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/api/notification/dismissAll", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      
      if (!response.ok) {
        throw new Error(`Failed to dismiss all notifications: ${response.status}`)
      }
      
      setNotifList([])
      // Fetch updated count immediately (before closing)
      await fetchNotificationCount(true)
      // Close modal after dismissing all
      onClose()
    } catch (err: any) {
      console.error("Error dismissing all notifications:", err)
      // Still clear UI on error
      setNotifList([])
      fetchNotificationCount(true)
      onClose()
    }
  }

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    setIsUserActive(true)

    // Clear existing idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current)
    }

    // Set new idle timeout (5 minutes)
    idleTimeoutRef.current = setTimeout(() => {
      setIsUserActive(false)
    }, 5 * 60 * 1000) // 5 minutes
  }, [])

  // Setup smart polling
  useEffect(() => {
    // Fetch immediately on mount (with count fetch)
    fetchNotifications(true)

    // Page Visibility API
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden
      setIsTabVisible(isVisible)

      if (isVisible) {
        // Tab became visible - fetch immediately and resume polling
        fetchNotifications(true)
        handleUserActivity() // Reset activity state
      }
    }

    // User activity events
    const activityEvents = ["mousemove", "keydown", "scroll", "click", "touchstart"]
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true })
    })

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Initial idle timeout setup
    handleUserActivity()

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleUserActivity)
      })
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
      }
    }
  }, [fetchNotifications, handleUserActivity])

  // Smart polling interval management
  useEffect(() => {
    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    // Don't poll if tab is hidden
    if (!isTabVisible) {
      return
    }

    // Determine polling interval based on user activity
    const interval = isUserActive ? 2 * 60 * 1000 : 5 * 60 * 1000 // 2 min active, 5 min idle

    pollingIntervalRef.current = setInterval(() => {
      if (isTabVisible) {
        // During polling, fetch notifications with count
        fetchNotifications(true)
      }
    }, interval)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [isTabVisible, isUserActive, fetchNotifications])

  // Fetch notifications when modal opens
  useEffect(() => {
    if (isOpen) {
      // Don't fetch count here - header already has it, and we'll fetch after notifications load
      fetchNotifications(true)
    }
  }, [isOpen, fetchNotifications])

  // Mark all as read when modal closes
  useEffect(() => {
    if (!isOpen) {
      markAllAsRead()
      // Cleanup debounce timeout on unmount/close
      if (countDebounceTimeoutRef.current) {
        clearTimeout(countDebounceTimeoutRef.current)
        countDebounceTimeoutRef.current = null
      }
    }
  }, [isOpen, markAllAsRead])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto [&_[data-slot='dialog-close']]:hidden">

        {/* Top-right custom close with hover dropdown */}
        <div className="absolute right-3 top-3 z-50">
          <div className="relative group">
            <button className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>

            {/* Dropdown menu - shows on hover */}
            <div className="absolute right-0 top-6 w-32 bg-white border rounded shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">

              {/* Close the dialog */}
              <button

                onClick={() => {
                  onClose()
                }}

                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
              >

                Close
              </button>
              <button

                onClick={dismissAll}

                disabled={notifList.length === 0 || loading}

                className="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >

                Dismiss All
              </button>
            </div>
          </div>
        </div>
        <DialogHeader className="flex items-center justify-center relative">
          <DialogTitle className="flex items-left gap-2">
            <Bell className="w-5 h-5 text-blue-600" />

            Notifications
            {notifList.length > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{notifList.length}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {loading && notifList.length === 0 && (
            <p className="text-center text-gray-500">Loading notifications...</p>
          )}

          {error && (
            <p className="text-center text-red-500 text-sm p-2 bg-red-50 rounded">
              {error}
            </p>
          )}

          {!loading && notifList.length === 0 && !error && (
            <p className="text-center text-gray-500">No notifications</p>
          )}

          {notifList.map((notification) => {
            const IconComponent = notification.icon
            return (
              <div

                key={notification.id}

                className={`p-4 rounded-lg border ${notification.border} border-opacity-50`}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full">
                    <IconComponent className={`w-4 h-4 ${notification.color}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{notification.title}</h4>
                    <p className="text-sm text-gray-700 mb-2">{notification.message}</p>

                    {notification.reason && (
                      <p className="text-sm p-2 rounded border-l-4 border-red-400">
                        <strong>Reason:</strong> {notification.reason}
                      </p>

                    )}
                    <p className="text-xs text-gray-500 mt-2">{notification.time}</p>
                  </div>
                  <button

                    onClick={() => dismissNotification(notification.id)}

                    disabled={loading}

                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>

  )

}
